import * as React from "react";
import { Checkbox, Input, Modal } from "antd";
import { InfoTooltip } from "../InfoTooltip";
import { K8S_CRD_DEFAULT_NAMESPACE } from "./viewModal";
import { itemHelpers as itemHelpersBase } from "../../mst/kinds/item.helpers";
import { Kind } from "../../schema/types/base";
import { parseLink } from "../../utils/linkParser/linkParser";
import { gvcScopedKinds } from "../../services/utils";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { useAppSelector } from "../../store/hooks";
import { selectContext } from "../../store/contextSlice";

interface Props {
  kind: Kind;
  links: string[];
  onClose?: () => void;
}

export const K8sCrdExportModal: React.FC<Props> = ({ kind, links, onClose }) => {
  const context = useAppSelector(selectContext);
  // For GVC-scoped kinds, only include GVC in filename if all items are from the same GVC
  let gvcForFilename = context.gvc;
  if (gvcScopedKinds.includes(kind) && links.length > 0) {
    const gvcSet = new Set(links.map(link => parseLink(link, { useInputCtx: true }).gvc).filter(Boolean));
    gvcForFilename = gvcSet.size === 1 ? Array.from(gvcSet)[0] : "";
  }
  const itemHelpers = itemHelpersBase({ org: context.org, gvc: gvcForFilename, kind: kind });

  // ANCHOR - States
  const [isOpen, setIsOpen] = React.useState<boolean>(true);
  const [k8sCrdNamespace, setK8sCrdNamespace] = React.useState<string>(K8S_CRD_DEFAULT_NAMESPACE);
  const [k8sCrdKeep, setK8sCrdKeep] = React.useState<boolean>(false);

  // ANCHOR - Functions
  async function onOk(): Promise<void> {
    // Hide modal
    setIsOpen(false);

    // Export to K8s CRD format
    await itemHelpers.handleExportK8sCrd(links, k8sCrdNamespace, k8sCrdKeep);

    // Close modal
    if (onClose) {
      onClose();
    }
  }

  return (
    <Modal
      title={<div className="mr-4 text-lg">Export To K8s CRD Format</div>}
      width={600}
      open={isOpen}
      onCancel={onClose}
      onOk={onOk}
      maskClosable={false}
      destroyOnHidden
      okText={`Export`}
    >
      <div className="flex flex-col gap-4">
        {/* Description */}
        <div className="flex flex-col gap-2">
          <div>
            Manage your Control Plane resources as Kubernetes Custom Resource Definitions (CRDs), using the{" "}
            <ExternalLink
              label="Control Plane K8s Operator"
              url={`https://github.com/controlplane-com/k8s-operator`}
              disableCopy
            />
            .
          </div>
          <div>Adjust the settings below if needed and click on the export button.</div>
        </div>

        {/* Input & Switch */}
        <div className="flex flex-col gap-3">
          {/* Namespace Label & Input */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center">
              <div>Namespace</div>
              <InfoTooltip
                title="Specify the Kubernetes namespace for the exported K8s CRD"
                placement="rightTop"
                arrow={{ pointAtCenter: true }}
              />
            </div>
            <Input value={k8sCrdNamespace} onChange={(e) => setK8sCrdNamespace(e.target.value)} />
          </div>

          {/* Keep Label & Switch */}
          <div className="flex items-center">
            <Checkbox checked={k8sCrdKeep} onChange={(e) => setK8sCrdKeep(e.target.checked)}>
              <span className="pl-1">Prevent deletion of the exported Control Plane K8s CRD</span>
            </Checkbox>
            <InfoTooltip
              title="Checking this will add the 'cpln.io/resource-policy: keep' annotation to the exported K8s CRD to prevent it from being deleted by the operator in case this resource is deleted on Kuberentes."
              placement="rightTop"
              arrow={{ pointAtCenter: true }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
